import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const analyticsApi = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth token from localStorage on every request
analyticsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken') || localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally
analyticsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('streamToken');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class AnalyticsService {
  // =====================
  // REAL-TIME VIEWERS
  // =====================
  
  static async getRealtimeViewers() {
    try {
      const response = await analyticsApi.get('/analytics/realtime/viewers');
      return response.data;
    } catch (error) {
      console.error('Error fetching real-time viewers:', error);
      throw error;
    }
  }

  // Full live snapshot: totals + per-stream + geo/device/referrer + 60-min trend
  static async getRealtime() {
    try {
      const response = await analyticsApi.get('/analytics/realtime');
      return response.data;
    } catch (error) {
      return { success: false };
    }
  }

  // =====================
  // WATCH TIME ANALYTICS
  // =====================
  
  static async getWatchTimeAnalytics(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/watchtime', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching watch time analytics:', error);
      throw error;
    }
  }

  // =====================
  // ENGAGEMENT RATE
  // =====================
  
  static async getEngagementAnalytics(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/engagement', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching engagement analytics:', error);
      throw error;
    }
  }

  // =====================
  // AUDIENCE LOCATION
  // =====================
  
  static async getLocationAnalytics(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/location', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching location analytics:', error);
      throw error;
    }
  }

  // =====================
  // DEVICE ANALYTICS
  // =====================
  
  static async getDeviceAnalytics(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/devices', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching device analytics:', error);
      throw error;
    }
  }

  // =====================
  // BANDWIDTH USAGE
  // =====================
  
  static async getBandwidthAnalytics(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/bandwidth', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching bandwidth analytics:', error);
      throw error;
    }
  }

  // =====================
  // REVENUE ANALYTICS (Admin only)
  // =====================
  
  static async getRevenueAnalytics(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/revenue', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching revenue analytics:', error);
      throw error;
    }
  }

  // =====================
  // HEATMAP DATA
  // =====================
  
  static async getHeatmapData(params = {}) {
    try {
      const response = await analyticsApi.get('/analytics/heatmap', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
      throw error;
    }
  }

  // =====================
  // DASHBOARD SUMMARY
  // =====================
  
  static async getDashboardSummary() {
    try {
      const response = await analyticsApi.get('/analytics/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  }

  // =====================
  // STREAM ANALYTICS
  // =====================
  
  static async getStreamAnalytics(streamId, params = {}) {
    try {
      const response = await analyticsApi.get(`/analytics/stream/${streamId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching stream analytics:', error);
      throw error;
    }
  }

  // =====================
  // TRACKING (for collecting data)
  // =====================
  
  static async trackSession(streamKey, viewerIp, metadata = {}) {
    try {
      const response = await analyticsApi.post('/analytics/track/session', {
        streamKey,
        viewerIp,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking session:', error);
      throw error;
    }
  }

  static async updateSession(sessionId, duration) {
    try {
      const response = await analyticsApi.put(`/analytics/track/session/${sessionId}`, {
        duration
      });
      return response.data;
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  static async trackEngagement(streamKey, engagementType, count = 1, metadata = {}) {
    try {
      const response = await analyticsApi.post('/analytics/track/engagement', {
        streamKey,
        engagementType,
        count,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking engagement:', error);
      throw error;
    }
  }
}

export default AnalyticsService;

